import {
    useGetLessonMaterialQuery,
    useUploadLessonMaterialMutation,
} from 'src/api/endpoints/lessons/lessons.api';
import { getLessonComponents } from 'src/pages/branch-teachers/HomeworksPage/functions/getLessonComponents';
import {
    LessonMaterial,
    LessonMaterialInformation,
} from 'src/api/endpoints/lessons/lessons.types';
import React, {
    FC,
    ReactElement,
    RefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import { LessonMaterialAttachingForm } from 'src/pages/Attendant/components/Modal';
import { notifyAboutException, notifyAboutSuccess } from 'src/utils/notify';
import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { AttendanceTable } from 'src/pages/Attendant/components/Table';
import { FormSubmitter } from 'src/pages/Attendant/form/form.types';
import styles from 'src/pages/Attendant/AttendantPage.module.scss';
import 'src/pages/Attendant/AttendantPage.module.scss';
import { skipToken } from '@reduxjs/toolkit/query';
import { LegacyModal } from 'src/components/Modal/LegacyModal';
import { useNumberParam } from 'src/hooks/params';
import { Nullable } from 'primereact/ts-helpers';
import Loader from '../../components/Loader';
import { Helmet } from 'react-helmet-async';
import { Button } from 'primereact/button';
import { FormItem } from './utils/utils';
import { Toast } from 'primereact/toast';
import { find, map, size } from 'lodash';
import {
    useGetAvailableGroupSubjectsWithAttendanceQuery,
    useGetAvailableGroupsWithAttendanceQuery,
    useGetGroupAttendanceLessonsBySubjectQuery,
    useGetStudentsAttendanceByLessonQuery,
    useUpdateStudentAttendanceMutation,
} from 'src/api/endpoints/attendance';
import {
    AttendanceLesson,
    AttendanceStatuses,
    RawStudentAttendance,
    StudentAttendance,
    UpdatingStudentAttendance,
    UpdatingStudentAttendanceBase,
} from 'src/@types/modules/attendance/attendance.types';
import { LessonStatus } from 'src/@types/modules/attendance/attendance.enums';
import { GroupShort } from 'src/@types/modules/groups/groups.types';
import { SubjectShort } from 'src/@types/modules/subjects/subjects.types';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';
import { MainSection } from 'src/components/design-system/struct/MainSection';
import { SecondaryButton } from 'src/components/design-system/form/buttons/SecondaryButton';
import { Link } from 'react-router-dom';
import { Col, Row, Select } from 'antd';
import { ArrowDownIcon } from 'src/icons/ArrowDown';

export const AttendantPage: FC<AuthorizedUserProps> = (
    props: AuthorizedUserProps
): ReactElement => {
    const toast: RefObject<Toast> = useRef<Toast>(null);

    const [uploadLessonMaterial] = useUploadLessonMaterialMutation();

    // region Groups
    // Получаем список доступных для выбора групп
    const [selectedGroup, selectGroup] = useState<GroupShort | null>(null);
    const [selectedGroupId, setSelectedGroupId] = useNumberParam<GroupShort>({
        fieldName: 'groupId',
        ref: selectedGroup,
    });

    const { data: groups, isLoading: groupsIsLoading } =
        useGetAvailableGroupsWithAttendanceQuery({
            params: { branchId: props.currentBranchId },
        });

    useEffect((): void => {
        if (selectedGroupId === undefined) return;

        // Если группы в текущий момент загружаются, то пропускаем эффект
        if (!groups || groupsIsLoading) return;

        const foundedGroup: Nullable<GroupShort> = groups.find(
            (group: GroupShort): boolean => group.id === selectedGroupId
        );

        // Если указанная в параметре группа не нашлась, то сбрасываем параметр
        if (!foundedGroup) return setSelectedGroupId(null);

        // Иначе выбираем группы
        selectGroup(foundedGroup);
    }, [groupsIsLoading, selectedGroupId, groups]);
    // endregion

    // region Subjects
    // Если группа выбрана, то мы получаем список доступных для просмотра посещаемости предметов
    const [selectedSubject, selectSubject] = useState<SubjectShort | null>(
        null
    );
    const [selectedSubjectId, setSelectedSubjectId] =
        useNumberParam<SubjectShort>({
            fieldName: 'subjectId',
            ref: selectedSubject,
        });

    const { data: subjects, isLoading: subjectsIsLoading } =
        useGetAvailableGroupSubjectsWithAttendanceQuery(
            selectedGroup ? { path: { groupId: selectedGroup.id } } : skipToken
        );

    useEffect((): void => {
        if (!selectedGroupId === null) return setSelectedSubjectId(null);

        if (!subjects || subjectsIsLoading) return;

        const foundedSubject: Nullable<SubjectShort> = subjects.find(
            (subject: SubjectShort): boolean => subject.id === selectedSubjectId
        );

        // Если указанный в параметре предмет не нашелся, то сбрасываем параметр
        if (!foundedSubject) return setSelectedSubjectId(null);

        // Иначе выбираем предмет группы
        selectSubject(foundedSubject);
    }, [subjectsIsLoading, selectedSubjectId, selectedGroupId, subjects]);
    // endregion

    // region AttendanceLessons
    // Если группа и предмет выбраны, то мы получаем список уроков выбранной группы по выбранному предмету
    const [lessons, setLessons] = useState<AttendanceLesson[] | void>();

    const [selectedLesson, selectLesson] = useState<AttendanceLesson | null>(
        null
    );
    const [selectedLessonId, setSelectedLessonId] =
        useNumberParam<AttendanceLesson>({
            fieldName: 'lessonId',
            ref: selectedLesson,
        });

    const { data: lessonsResponse, isLoading: lessonsIsLoading } =
        useGetGroupAttendanceLessonsBySubjectQuery(
            selectedGroup && selectedSubject
                ? {
                      path: {
                          groupId: selectedGroup.id,
                          subjectId: selectedSubject.id,
                      },
                  }
                : skipToken
        );

    useEffect((): void => {
        if (!selectedSubjectId === null) return setSelectedLessonId(null);

        // Если группы в текущий момент загружаются, то пропускаем эффект
        if (!lessonsResponse || lessonsIsLoading) return;
        if (!lessons) return;

        const foundedLesson: Nullable<AttendanceLesson> = lessons.find(
            (lesson: AttendanceLesson): boolean =>
                lesson.id === selectedLessonId
        );

        // Если указанная в параметре группа не нашлась, то сбрасываем параметр
        if (!foundedLesson) return setSelectedLessonId(null);

        // Иначе выбираем группы
        selectLesson(foundedLesson);
    }, [selectedSubjectId, selectedLessonId, lessonsIsLoading, lessons]);

    useEffect((): void => {
        if (!lessonsResponse || lessonsIsLoading) return;

        setLessons(lessonsResponse);
    }, [lessonsResponse, lessonsIsLoading]);
    // endregion

    // region LessonMaterial attach information
    const [lessonHasMaterial, setLessonHasMaterial] = useState<boolean>(false);
    const [lessonMaterial, setLessonMaterial] =
        useState<LessonMaterialInformation>({
            attachment: null,
            isAttached: false,
        });

    const {
        data: lessonAttachedMaterial,
        isLoading: isLessonAttachedMaterialIsLoading,
    } = useGetLessonMaterialQuery(
        selectedLesson
            ? {
                  path: { lessonId: selectedLesson.id },
              }
            : skipToken
    );

    useEffect((): void => {
        if (isLessonAttachedMaterialIsLoading || !lessonAttachedMaterial)
            return;

        setLessonMaterial(lessonAttachedMaterial);
        setLessonHasMaterial(lessonAttachedMaterial.isAttached);
    }, [isLessonAttachedMaterialIsLoading, lessonAttachedMaterial]);
    // endregion

    const [isLessonMaterialModalVisible, setLessonMaterialModalVisible] =
        useState(false);

    const [studentsAttendance, setStudentsAttendance] = useState<
        StudentAttendance[]
    >([]);

    const {
        data: studentsAttendanceResponse,
        isLoading: studentsAttendanceResponseIsLoading,
    } = useGetStudentsAttendanceByLessonQuery(
        selectedLesson ? { path: { lessonId: selectedLesson.id } } : skipToken
    );

    const [updateStudentAttendance] = useUpdateStudentAttendanceMutation();

    useEffect((): void => {
        if (!studentsAttendanceResponse || studentsAttendanceResponseIsLoading)
            return;

        setStudentsAttendance(studentsAttendanceResponse);
    }, [studentsAttendanceResponse, studentsAttendanceResponseIsLoading]);

    const transformStudentAttendance = (
        attendance: RawStudentAttendance
    ): UpdatingStudentAttendance => {
        const partialAttendance: UpdatingStudentAttendanceBase = {
            status: attendance.status,
        };

        if (attendance.status === AttendanceStatuses.Absent) {
            return {
                ...partialAttendance,
                mark: 0,
                reward: 0,
                comment: null,
            };
        }

        return {
            ...partialAttendance,
            mark: attendance.mark,
            reward: attendance.reward,
            comment: attendance.comment,
        };
    };

    const onStudentAttendanceChanged = (
        attendance: RawStudentAttendance
    ): void => {
        if (!selectedLesson) return;

        updateStudentAttendance({
            path: {
                lessonId: selectedLesson.id,
                attendanceId: attendance.id,
            },
            body: transformStudentAttendance(attendance),
        })
            .unwrap()
            .then((updatedStudentAttendance: StudentAttendance): void => {
                setStudentsAttendance(
                    (previous: StudentAttendance[]): StudentAttendance[] =>
                        previous.map(
                            (
                                attendance: StudentAttendance
                            ): StudentAttendance => {
                                if (
                                    attendance.id ===
                                    updatedStudentAttendance.id
                                ) {
                                    return updatedStudentAttendance;
                                }
                                return attendance;
                            }
                        )
                );
            })
            .catch((reason: any): void => {
                notifyAboutException({
                    toast: toast,
                    content: reason.data,
                });
            });
    };

    const resetLessons: VoidFunction = (): void => {
        selectLesson(null);
        setLessons();
    };

    const resetSubject: VoidFunction = (): void => {
        resetLessons();
        setSelectedSubjectId(null);
        selectSubject(null);
    };

    const onSuccessLessonMaterialUploading: VoidFunction = (): void => {
        notifyAboutSuccess({
            toast: toast,
            content: 'Материал урока успешно обновлен',
        });

        setLessonMaterialModalVisible(false);
    };

    const onErrorWhenLessonMaterialUploading = (reason: any): void => {
        notifyAboutException({
            toast: toast,
            content: reason.data,
        });
    };

    const onLessonMaterialFormSubmit: FormSubmitter<LessonMaterial<Date>> = (
        form: LessonMaterial<Date>
    ): void => {
        if (!selectedLesson) return;

        uploadLessonMaterial({
            path: {
                lessonId: selectedLesson.id,
            },
            body: {
                materialId: form.material.id,
                expiresAt: form.expiresAt?.getTime() || null,
            },
        })
            .unwrap()
            .then(onSuccessLessonMaterialUploading)
            .catch(onErrorWhenLessonMaterialUploading);
    };

    return (
        <StudentLayout auth={props}>
            <Toast ref={toast} />
            <Helmet
                title={`Посещаемость · ${props.currentBranch.name} · Личный кабинет`}
            />

            <MainSection
                title={'Посещаемость'}
                actions={
                    <>
                        <Link to={'history'}>
                            <SecondaryButton label={'История'} />
                        </Link>
                    </>
                }
            >
                {selectedLesson && lessonMaterial && (
                    <LegacyModal
                        header={'Методический материал'}
                        visible={isLessonMaterialModalVisible}
                        onHide={(): void =>
                            setLessonMaterialModalVisible(false)
                        }
                    >
                        <LessonMaterialAttachingForm
                            lessonId={selectedLesson.id}
                            initialValues={lessonMaterial}
                            onSubmit={onLessonMaterialFormSubmit}
                        />
                    </LegacyModal>
                )}

                {groupsIsLoading || groups === undefined ? (
                    <Loader />
                ) : (
                    <>
                        <Row
                            style={{ width: '100%' }}
                            gutter={[{ xs: 0, sm: 10 }, 10]}
                        >
                            <Col xs={24} sm={12} md={8}>
                                <Select
                                    suffixIcon={
                                        <ArrowDownIcon
                                            className={'select-input-icon'}
                                            color={'#092433'}
                                        />
                                    }
                                    showSearch={true}
                                    optionFilterProp={'label'}
                                    classNames={{
                                        root: 'select-input range-mode-input',
                                    }}
                                    options={map(groups, (g: GroupShort) => ({
                                        label: g.name,
                                        value: g.id,
                                    }))}
                                    onSelect={(id: number) => {
                                        resetSubject();
                                        selectGroup(
                                            find(
                                                groups,
                                                (g: GroupShort) => g.id === id
                                            )!
                                        );
                                    }}
                                    value={selectedGroup?.id}
                                    placeholder={'Выберите группу'}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Select
                                    suffixIcon={
                                        <ArrowDownIcon
                                            className={'select-input-icon'}
                                            color={'#092433'}
                                        />
                                    }
                                    showSearch={true}
                                    optionFilterProp={'label'}
                                    classNames={{
                                        root: 'select-input range-mode-input',
                                    }}
                                    options={map(
                                        subjects,
                                        (s: SubjectShort) => ({
                                            label: s.name,
                                            value: s.id,
                                        })
                                    )}
                                    onSelect={(id: number) => {
                                        resetLessons();
                                        selectSubject(
                                            find(
                                                subjects,
                                                (s: SubjectShort) => s.id === id
                                            )!
                                        );
                                    }}
                                    value={selectedSubject?.id}
                                    placeholder={'Выберите предмет'}
                                    disabled={selectedGroup === null}
                                />
                            </Col>
                        </Row>

                        {lessons && size(lessons) ? (
                            <FormItem label={'Урок №'}>
                                <div className={styles.classWrapper}>
                                    {getLessonComponents<AttendanceLesson>({
                                        lessons,
                                        onLessonClick: (
                                            lesson: AttendanceLesson
                                        ): void => {
                                            selectLesson(lesson);
                                        },
                                        isSelected: (
                                            lesson: AttendanceLesson
                                        ): boolean => {
                                            return (
                                                lesson.id === selectedLesson?.id
                                            );
                                        },
                                        isDisabled: (
                                            lesson: AttendanceLesson
                                        ): boolean => {
                                            return (
                                                lesson.status ===
                                                LessonStatus.Scheduled
                                            );
                                        },
                                    })}
                                </div>
                            </FormItem>
                        ) : null}
                    </>
                )}

                {selectedLesson ? (
                    isLessonAttachedMaterialIsLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <FormItem label={'Методический пакет'}>
                                <Button
                                    onClick={(): void =>
                                        setLessonMaterialModalVisible(true)
                                    }
                                >
                                    {lessonHasMaterial
                                        ? 'Обновить'
                                        : 'Загрузить'}
                                </Button>
                            </FormItem>

                            {studentsAttendanceResponseIsLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    {size(studentsAttendance) ? (
                                        <>
                                            <AttendanceTable
                                                statusLoading={
                                                    studentsAttendanceResponseIsLoading
                                                }
                                                attendance={studentsAttendance}
                                                onAttendanceChange={
                                                    onStudentAttendanceChanged
                                                }
                                            />
                                        </>
                                    ) : null}
                                </>
                            )}
                        </>
                    )
                ) : null}
            </MainSection>
        </StudentLayout>
    );
};
